import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Check,
  Church,
  Drum,
  GraduationCap,
  Heart,
  Mic2,
  Sparkles,
  Users,
} from "lucide-react";
import programs from "../data/programsData";
import SEO from "../components/SEO";
import WhatsAppButton from "../components/WhatsAppButton";
import { PAGE_SEO } from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";
import { BRAND_NAME } from "../data/contactInfo";
import { publicImage } from "../data/pageImages";
import "../styles/Programs.css";

const CONTACT_SEARCH = { pathname: "/contact", search: "?type=wedding&form=open" };
const HERO_IMAGE = "gallery-temple-shrine-group.jpeg";

const TAG_ICONS = {
  Sacred: Church,
  Melam: Drum,
  Ensemble: Mic2,
  Corporate: Building2,
  Celebration: Heart,
  Concert: Sparkles,
  Education: GraduationCap,
  Fusion: Users,
};

const useReveal = () => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.classList.add("is-visible");
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return ref;
};

const Reveal = ({ className = "", children, delay = 0 }) => {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`prog-reveal ${className}`.trim()}
      style={{ "--reveal-delay": `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Programs = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const active = programs[activeIndex];
  const ActiveIcon = TAG_ICONS[active.tag] || Sparkles;

  const selectProgram = (index) => {
    if (index === activeIndex) return;
    setActiveIndex(index);
    setAnimKey((k) => k + 1);
  };

  return (
    <main className="prog-page">
      <SEO
        title={PAGE_SEO.programs.title}
        description={PAGE_SEO.programs.description}
        path={PAGE_SEO.programs.path}
        ogImage={SEO_IMAGES.programs}
      />

      <header className="prog-hero">
        <img
          className="prog-hero__media"
          src={publicImage(HERO_IMAGE)}
          alt="Kerala Chenda Melam program ensemble"
          width={1600}
          height={900}
          fetchPriority="high"
        />
        <div className="prog-hero__veil" aria-hidden="true" />
        <div className="prog-hero__shine" aria-hidden="true" />
        <div className="prog-hero__content">
          <p className="prog-hero__brand">{BRAND_NAME}</p>
          <h1 className="prog-hero__title">Chenda Melam Programs in Chennai</h1>
          <p className="prog-hero__lead">
            Temple festivals, weddings, corporate stages, and cultural concerts — authentic Kerala
            Melam ensembles scaled to your occasion.
          </p>
          <div className="prog-hero__actions">
            <WhatsAppButton>Book a Program</WhatsAppButton>
            <Link to="/chenda-melam-chennai" className="prog-btn prog-btn--ghost">
              Wedding Melam
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      <section className="prog-section prog-intro" aria-labelledby="prog-intro-heading">
        <div className="prog-section__inner">
          <Reveal>
            <p className="prog-kicker">What we offer</p>
            <h2 id="prog-intro-heading" className="prog-heading">
              Ensembles for every stage
            </h2>
            <p className="prog-lead">
              With over two decades of performance experience, Guru Thiruvambady Vineesh Maarar leads
              ensembles for temple festivals, weddings, corporate events, school workshops, and concert
              stages — from an intimate 6-artist team to a grand 100+ artist Melam.
            </p>
          </Reveal>

          <div className="prog-stats">
            {[
              { num: "8+", label: "Program types" },
              { num: "100+", label: "Artists available" },
              { num: "20+", label: "Years experience" },
            ].map((stat, i) => (
              <Reveal key={stat.label} className="prog-stat" delay={120 * (i + 1)}>
                <span className="prog-stat__num">{stat.num}</span>
                <span className="prog-stat__label">{stat.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="prog-section prog-explorer" aria-labelledby="prog-explorer-heading">
        <div className="prog-section__inner">
          <Reveal>
            <p className="prog-kicker">Programs</p>
            <h2 id="prog-explorer-heading" className="prog-heading">
              Choose a program format
            </h2>
            <p className="prog-lead">
              Hover or tap a program — the stage updates with photos, highlights, and details.
            </p>
          </Reveal>

          <div className="prog-explorer__layout">
            <ul className="prog-rail" role="listbox" aria-label="Program types">
              {programs.map((program, index) => {
                const Icon = TAG_ICONS[program.tag] || Sparkles;
                return (
                  <li key={program.title} role="option" aria-selected={index === activeIndex}>
                    <button
                      type="button"
                      className={`prog-rail__tab${index === activeIndex ? " is-active" : ""}`}
                      onClick={() => selectProgram(index)}
                      onMouseEnter={() => selectProgram(index)}
                      style={{ animationDelay: `${index * 60}ms` }}
                    >
                      <span className="prog-rail__icon" aria-hidden="true">
                        <Icon size={16} strokeWidth={1.75} />
                      </span>
                      <span className="prog-rail__copy">
                        <span className="prog-rail__name">{program.title}</span>
                        <span className="prog-rail__meta">{program.tag}</span>
                      </span>
                      <span className="prog-rail__bar" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>

            <article className="prog-stage" aria-live="polite">
              <div className="prog-stage__visual" key={`img-${animKey}`}>
                <img src={active.imageSrc} alt={active.title} />
                <div className="prog-stage__veil" aria-hidden="true" />
                <span className="prog-stage__badge">
                  <ActiveIcon size={14} aria-hidden="true" />
                  {active.tag}
                </span>
              </div>

              <div className="prog-stage__detail" key={`detail-${animKey}`}>
                <h3>{active.title}</h3>
                <p className="prog-stage__desc">{active.desc}</p>
                <ul className="prog-stage__highlights">
                  {active.highlights.map((h, i) => (
                    <li key={h} style={{ animationDelay: `${140 + i * 70}ms` }}>
                      <Check size={15} aria-hidden="true" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
                <p className="prog-stage__details">{active.details}</p>
                <div className="prog-stage__actions">
                  <WhatsAppButton size="sm">Enquire on WhatsApp</WhatsAppButton>
                  <Link to={CONTACT_SEARCH} className="prog-text-link">
                    Request a quote
                    <ArrowRight size={15} aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="prog-section prog-mosaic" aria-label="Program moments">
        <div className="prog-section__inner">
          <Reveal>
            <p className="prog-kicker">In action</p>
            <h2 className="prog-heading">Moments from our programs</h2>
          </Reveal>
          <div className="prog-mosaic__grid">
            {programs.slice(0, 6).map((p, i) => (
              <Reveal
                key={p.title}
                className={`prog-mosaic__item${i === 0 ? " prog-mosaic__item--lead" : ""}`}
                delay={80 * i}
              >
                <button type="button" onClick={() => selectProgram(i)}>
                  <img src={p.imageSrc} alt={p.title} loading="lazy" />
                  <span>{p.title}</span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="prog-cta-band" aria-labelledby="prog-cta-heading">
        <Reveal>
          <div className="prog-cta-band__inner">
            <h2 id="prog-cta-heading">Book a program</h2>
            <p>
              Tell us your event type, date, venue, and audience size — we will recommend the right
              ensemble and send a proposal.
            </p>
            <div className="prog-hero__actions">
              <WhatsAppButton>WhatsApp Now</WhatsAppButton>
              <Link to="/fusions" className="prog-btn prog-btn--ghost">
                Explore Fusion
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <Link to="/melams" className="prog-btn prog-btn--ghost">
                Melam Forms
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Programs;
